import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Chip, Card, Stack, Button, IconButton, useMediaQuery, useTheme,
  Drawer, Divider, ToggleButton, ToggleButtonGroup, Skeleton,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import LaptopMacOutlinedIcon from "@mui/icons-material/LaptopMacOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import { useGetMyStudentCalendarQuery } from "../../Api/attoDeskApi";

/**
 * Shared Staff Portal student calendar. Consumed twice on the profile
 * page — once as `compact` inside the Dashboard tab and once at full
 * width inside the Schedule tab. Server-authoritative Staff scope:
 * every event is guaranteed by StaffTeachingScopeService::canAccessStudent
 * plus staff_id-clamped occurrence lookup in studentCalendar.
 */

const NAVY = "#1b3a6b";
const NAVY_2 = "#2a5298";

type CalendarEvent = {
  occurrence_id: number;
  // class_id is the class TEMPLATE / SCHEDULE id. Used by the drawer's
  // "View Full Class Details" CTA. NEVER pass occurrence_id to a
  // /class-schedules/:classId route — different id family.
  class_id: number | null;
  class_label: string;
  scheduled_start_at: string;
  scheduled_end_at: string;
  scheduled_date: string;
  location: string | null;
  status: string;
  attendance_status: string | null;
  actual_end_at: string | null;
  subject?: string | null;
  grade?: string | null;
  staff_name?: string | null;
  class_type?: string | null;
  teaching_mode?: string | null;
  schedule_type?: string | null;
  is_cancelled?: boolean;
};

type ViewKey = "month" | "week" | "agenda";
type StudentContext = { id: number; name: string; grade: string | null };
type Props = { studentId: number; compact?: boolean; student?: StudentContext };

const isoYMD = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const startOfWeekSun = (d: Date) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const monthLabel = (d: Date) => d.toLocaleDateString(undefined, { month: "long", year: "numeric" });

// Status → colour mapping. Attendance status wins over occurrence
// status when present (a completed/marked class is more informative
// than the raw scheduled/completed flag).
const eventStyle = (e: CalendarEvent) => {
  const att = (e.attendance_status || "").toLowerCase();
  const st  = (e.status || "").toLowerCase();
  if (st === "cancelled") return { bg: "#fee2e2", fg: "#991b1b", dot: "#dc2626", label: "Cancelled" };
  if (att === "present" || att === "late") return { bg: "#dcfce7", fg: "#166534", dot: "#16a34a", label: att === "late" ? "Late" : "Present" };
  if (att === "absent") return { bg: "#fee2e2", fg: "#991b1b", dot: "#dc2626", label: "Absent" };
  if (st === "completed" && !att) return { bg: "#fef3c7", fg: "#92400e", dot: "#d97706", label: "Attendance Pending" };
  if (st === "makeup") return { bg: "#f3e8ff", fg: "#6b21a8", dot: "#7c3aed", label: "Makeup" };
  return { bg: "#eef2ff", fg: NAVY, dot: NAVY_2, label: "Scheduled" };
};

const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const fmtDateFull = (iso: string) => new Date(iso).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
const fmtDateShort = (iso: string) => new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

const StaffStudentCalendar: React.FC<Props> = ({ studentId, compact = false, student }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Mobile always defaults to agenda — the 7-column grid squeezes to
  // unusable widths at 390px.
  const [view, setView] = useState<ViewKey>(isMobile ? "agenda" : "month");
  const [anchorDate, setAnchorDate] = useState<Date>(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

  // Compute the fetched window based on the current view. Slightly
  // padded so month/week edges show the neighbouring days that visually
  // fill the grid (leading/trailing greys).
  const window = useMemo(() => {
    if (view === "week") {
      const start = startOfWeekSun(anchorDate);
      return { from: isoYMD(start), to: isoYMD(addDays(start, 6)) };
    }
    if (view === "agenda") {
      const start = addDays(today, -14);
      const end = addDays(today, 45);
      return { from: isoYMD(start), to: isoYMD(end) };
    }
    // month: pad to the visible grid (up to ~6 rows starting from Sunday)
    const first = startOfMonth(anchorDate);
    const last = endOfMonth(anchorDate);
    const gridStart = startOfWeekSun(first);
    const gridEnd = addDays(gridStart, 41); // 6 weeks
    // Cap at end of month + 7 so the grid is always full-width but the
    // range fits within the backend's 62-day guard.
    const cap = addDays(last, 7);
    return { from: isoYMD(gridStart), to: isoYMD(cap < gridEnd ? cap : gridEnd) };
  }, [view, anchorDate, today]);

  const { data, isFetching } = useGetMyStudentCalendarQuery(
    { studentId, from: window.from, to: window.to },
    { skip: !studentId },
  );
  const events: CalendarEvent[] = useMemo(() => {
    const raw = (data as any)?.data?.events;
    return Array.isArray(raw) ? raw as CalendarEvent[] : [];
  }, [data]);

  // Bucket events by YMD for O(1) day lookups.
  const eventsByDay = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    for (const ev of events) {
      const key = ev.scheduled_date;
      (map[key] ||= []).push(ev);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => a.scheduled_start_at.localeCompare(b.scheduled_start_at));
    }
    return map;
  }, [events]);

  // Chips summary — real counts over the visible events set.
  const counts = useMemo(() => {
    let upcoming = 0, attended = 0, cancelled = 0;
    const now = Date.now();
    for (const e of events) {
      if ((e.status || "").toLowerCase() === "cancelled") { cancelled++; continue; }
      const att = (e.attendance_status || "").toLowerCase();
      if (att === "present" || att === "late") attended++;
      if (new Date(e.scheduled_start_at).getTime() >= now) upcoming++;
    }
    return { total: events.length, upcoming, attended, cancelled };
  }, [events]);

  // Event drawer state. Replaced the previous tiny popover so the
  // Staff Student Profile calendar shows a proper occurrence-details
  // sheet before the teacher can jump into attendance — matching the
  // Admin student-calendar interaction pattern. The drawer's primary
  // CTA is "View Full Class Details" (uses class_id), never a direct
  // "Take Attendance" (that action lives on the full class page).
  const [drawerEvent, setDrawerEvent] = useState<CalendarEvent | null>(null);
  const openEvent = (_target: HTMLElement, event: CalendarEvent) => setDrawerEvent(event);
  const closeEvent = () => setDrawerEvent(null);

  const goToday = () => setAnchorDate(new Date());
  const goPrev = () => {
    if (view === "month") setAnchorDate(new Date(anchorDate.getFullYear(), anchorDate.getMonth() - 1, 1));
    else if (view === "week") setAnchorDate(addDays(anchorDate, -7));
    else setAnchorDate(addDays(anchorDate, -14));
  };
  const goNext = () => {
    if (view === "month") setAnchorDate(new Date(anchorDate.getFullYear(), anchorDate.getMonth() + 1, 1));
    else if (view === "week") setAnchorDate(addDays(anchorDate, 7));
    else setAnchorDate(addDays(anchorDate, 14));
  };

  return (
    <Card sx={{ p: 2.25, borderRadius: 2.5, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(15,23,42,0.04)" }}>
      {/* Header */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarMonthOutlinedIcon sx={{ color: NAVY, fontSize: 20 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, textTransform: "uppercase", letterSpacing: "0.05em" }}>Class Calendar</Typography>
          </Stack>
          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
            View scheduled classes and attendance for this student.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button onClick={goToday} size="small" sx={{ textTransform: "none", fontWeight: 700, color: NAVY, border: "1px solid #e2e8f0", borderRadius: 2, px: 1.5 }}>Today</Button>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_e, v) => v && setView(v as ViewKey)}
            sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 700, fontSize: 12, px: 1.5, color: "#475569", border: "1px solid #e2e8f0" }, "& .Mui-selected": { bgcolor: `${NAVY} !important`, color: "#fff !important" } }}
          >
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
            <ToggleButton value="agenda">Agenda</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {/* Nav + summary chips */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" sx={{ mb: 1.25 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <IconButton size="small" onClick={goPrev} sx={{ border: "1px solid #e2e8f0" }}><ChevronLeftIcon fontSize="small" /></IconButton>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#0f172a", mx: 1, minWidth: 150, textAlign: "center" }}>
            {view === "week"
              ? `${fmtDateShort(startOfWeekSun(anchorDate).toISOString())} – ${fmtDateShort(addDays(startOfWeekSun(anchorDate), 6).toISOString())}`
              : monthLabel(anchorDate)}
          </Typography>
          <IconButton size="small" onClick={goNext} sx={{ border: "1px solid #e2e8f0" }}><ChevronRightIcon fontSize="small" /></IconButton>
        </Stack>
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }}>
          <Chip size="small" label={`${counts.total} IN VIEW`} sx={{ bgcolor: "#eef2ff", color: NAVY, fontWeight: 800, fontSize: 10.5 }} />
          <Chip size="small" label={`${counts.upcoming} UPCOMING`} sx={{ bgcolor: "#e0f2fe", color: "#075985", fontWeight: 800, fontSize: 10.5 }} />
          <Chip size="small" label={`${counts.attended} ATTENDED`} sx={{ bgcolor: "#dcfce7", color: "#166534", fontWeight: 800, fontSize: 10.5 }} />
          <Chip size="small" label={`${counts.cancelled} CANCELLED`} sx={{ bgcolor: "#fee2e2", color: "#991b1b", fontWeight: 800, fontSize: 10.5 }} />
        </Stack>
      </Stack>

      {isFetching && !events.length ? (
        <Skeleton variant="rounded" height={compact ? 260 : 380} />
      ) : view === "month" ? (
        <MonthGrid anchorDate={anchorDate} eventsByDay={eventsByDay} onEventClick={openEvent} today={today} compact={compact} />
      ) : view === "week" ? (
        <WeekGrid anchorDate={anchorDate} eventsByDay={eventsByDay} onEventClick={openEvent} today={today} />
      ) : (
        <AgendaList events={events} onEventClick={openEvent} />
      )}

      {events.length === 0 && !isFetching && (
        <Box sx={{ textAlign: "center", py: 3, color: "text.secondary" }}>
          <Typography sx={{ fontSize: 13 }}>
            {view === "month" ? "No classes scheduled for this month." : view === "week" ? "No classes this week." : "No classes in the near future."}
          </Typography>
        </Box>
      )}

      <EventDetailsDrawer event={drawerEvent} onClose={closeEvent} studentId={studentId} student={student} />

    </Card>
  );
};

const MonthGrid: React.FC<{
  anchorDate: Date;
  eventsByDay: Record<string, CalendarEvent[]>;
  onEventClick: (target: HTMLElement, e: CalendarEvent) => void;
  today: Date;
  compact: boolean;
}> = ({ anchorDate, eventsByDay, onEventClick, today, compact }) => {
  const days = useMemo(() => {
    const first = startOfMonth(anchorDate);
    const gridStart = startOfWeekSun(first);
    return Array.from({ length: 42 }).map((_, i) => addDays(gridStart, i));
  }, [anchorDate]);
  const currentMonth = anchorDate.getMonth();
  const cellMinHeight = compact ? 74 : 108;
  const maxEventsPerCell = compact ? 2 : 3;

  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5, mb: 0.5 }}>
        {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((d) => (
          <Box key={d} sx={{ textAlign: "center", fontSize: 10.5, fontWeight: 800, color: "#64748b", letterSpacing: "0.06em", py: 0.75 }}>{d}</Box>
        ))}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5, border: "1px solid #e2e8f0", borderRadius: 2, overflow: "hidden" }}>
        {days.map((d, idx) => {
          const key = isoYMD(d);
          const dayEvents = eventsByDay[key] || [];
          const inMonth = d.getMonth() === currentMonth;
          const isToday = sameDay(d, today);
          return (
            <Box key={idx} sx={{
              minHeight: cellMinHeight,
              bgcolor: inMonth ? "#fff" : "#f8fafc",
              borderRight: (idx + 1) % 7 === 0 ? "none" : "1px solid #f1f5f9",
              borderBottom: idx >= 35 ? "none" : "1px solid #f1f5f9",
              p: 0.5, display: "flex", flexDirection: "column", gap: 0.25,
            }}>
              <Box sx={{ textAlign: "right", mb: 0.25 }}>
                <Box component="span" sx={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 22, height: 22, borderRadius: "50%",
                  fontSize: 11.5, fontWeight: isToday ? 800 : 600,
                  color: isToday ? "#fff" : inMonth ? "#0f172a" : "#94a3b8",
                  bgcolor: isToday ? NAVY : "transparent",
                }}>{d.getDate()}</Box>
              </Box>
              <Stack spacing={0.25}>
                {dayEvents.slice(0, maxEventsPerCell).map((e) => {
                  const s = eventStyle(e);
                  return (
                    <Box
                      key={e.occurrence_id}
                      onClick={(evt) => onEventClick(evt.currentTarget as HTMLElement, e)}
                      sx={{
                        cursor: "pointer",
                        px: 0.75, py: 0.25, borderRadius: 1,
                        bgcolor: s.bg, color: s.fg,
                        fontSize: 10.5, fontWeight: 700,
                        display: "flex", gap: 0.5, alignItems: "center",
                        overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
                        borderLeft: `2px solid ${s.dot}`,
                        "&:hover": { filter: "brightness(0.97)" },
                      }}
                    >
                      <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>{fmtTime(e.scheduled_start_at)}</Box>
                      <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", fontWeight: 600 }}>{e.class_label}</Box>
                    </Box>
                  );
                })}
                {dayEvents.length > maxEventsPerCell && (
                  <Typography sx={{ fontSize: 10, color: "#64748b", fontWeight: 700, pl: 0.75 }}>
                    +{dayEvents.length - maxEventsPerCell} more
                  </Typography>
                )}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const WeekGrid: React.FC<{
  anchorDate: Date;
  eventsByDay: Record<string, CalendarEvent[]>;
  onEventClick: (target: HTMLElement, e: CalendarEvent) => void;
  today: Date;
}> = ({ anchorDate, eventsByDay, onEventClick, today }) => {
  const start = useMemo(() => startOfWeekSun(anchorDate), [anchorDate]);
  const days = useMemo(() => Array.from({ length: 7 }).map((_, i) => addDays(start, i)), [start]);
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.75, border: "1px solid #e2e8f0", borderRadius: 2, p: 0.75, bgcolor: "#f8fafc" }}>
      {days.map((d) => {
        const key = isoYMD(d);
        const dayEvents = eventsByDay[key] || [];
        const isToday = sameDay(d, today);
        return (
          <Box key={key} sx={{ bgcolor: "#fff", borderRadius: 1.5, border: "1px solid #e2e8f0", minHeight: 200, p: 1, display: "flex", flexDirection: "column" }}>
            <Box sx={{ textAlign: "center", mb: 1 }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "#64748b", letterSpacing: "0.05em" }}>{d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase()}</Typography>
              <Box component="span" sx={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 26, height: 26, borderRadius: "50%",
                fontSize: 13, fontWeight: isToday ? 800 : 700,
                color: isToday ? "#fff" : "#0f172a",
                bgcolor: isToday ? NAVY : "transparent", mt: 0.25,
              }}>{d.getDate()}</Box>
            </Box>
            <Stack spacing={0.5}>
              {dayEvents.map((e) => {
                const s = eventStyle(e);
                return (
                  <Box
                    key={e.occurrence_id}
                    onClick={(evt) => onEventClick(evt.currentTarget as HTMLElement, e)}
                    sx={{
                      cursor: "pointer", p: 1, borderRadius: 1.5,
                      bgcolor: s.bg, color: s.fg,
                      borderLeft: `3px solid ${s.dot}`,
                      "&:hover": { filter: "brightness(0.97)" },
                    }}
                  >
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtTime(e.scheduled_start_at)} – {fmtTime(e.scheduled_end_at)}</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, mt: 0.25 }}>{e.class_label}</Typography>
                  </Box>
                );
              })}
              {dayEvents.length === 0 && (
                <Typography sx={{ fontSize: 11, color: "#94a3b8", textAlign: "center", py: 1 }}>—</Typography>
              )}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};

const AgendaList: React.FC<{ events: CalendarEvent[]; onEventClick: (target: HTMLElement, e: CalendarEvent) => void }> = ({ events, onEventClick }) => {
  const grouped = useMemo(() => {
    const out: Array<{ dateKey: string; label: string; items: CalendarEvent[] }> = [];
    const map: Record<string, CalendarEvent[]> = {};
    for (const e of events) (map[e.scheduled_date] ||= []).push(e);
    const keys = Object.keys(map).sort();
    for (const k of keys) {
      const d = new Date(k + "T00:00:00");
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
      const label = diff === 0 ? "Today" : diff === 1 ? "Tomorrow" : diff === -1 ? "Yesterday" : d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
      out.push({ dateKey: k, label, items: map[k] });
    }
    return out;
  }, [events]);

  if (grouped.length === 0) return null;
  return (
    <Stack spacing={1.5}>
      {grouped.map((g) => (
        <Box key={g.dateKey}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5 }}>{g.label}</Typography>
          <Stack spacing={0.5}>
            {g.items.map((e) => {
              const s = eventStyle(e);
              return (
                <Box
                  key={e.occurrence_id}
                  onClick={(evt) => onEventClick(evt.currentTarget as HTMLElement, e)}
                  sx={{ display: "flex", alignItems: "center", gap: 1.25, p: 1, borderRadius: 1.5, border: "1px solid #e2e8f0", cursor: "pointer", "&:hover": { bgcolor: "#f8fafc" } }}
                >
                  <Box sx={{ width: 3, alignSelf: "stretch", bgcolor: s.dot, borderRadius: 4 }} />
                  <Box sx={{ minWidth: 92 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: NAVY, fontVariantNumeric: "tabular-nums" }}>{fmtTime(e.scheduled_start_at)}</Typography>
                    <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>{fmtTime(e.scheduled_end_at)}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.class_label}</Typography>
                    {e.location && <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{e.location}</Typography>}
                  </Box>
                  <Chip size="small" label={s.label} sx={{ bgcolor: s.bg, color: s.fg, fontWeight: 800, fontSize: 10.5, height: 22 }} />
                </Box>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

// ── Class occurrence details drawer ──────────────────────────────────
// Staff-safe: shows only fields the studentCalendar endpoint returns
// (all denormalized snapshots or already-scoped values). No admin-only
// actions (Edit Class / Add Student / Switch Staff / Delete Class).
// Primary CTA navigates by CLASS_ID to the shared class detail page,
// which is where Mark Attendance (per-occurrence) lives.
const EventDetailsDrawer: React.FC<{ event: CalendarEvent | null; onClose: () => void; studentId: number; student?: StudentContext }> = ({ event, onClose, studentId, student }) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(useTheme().breakpoints.down("sm"));
  if (!event) {
    return <Drawer anchor="right" open={false} onClose={onClose}><Box /></Drawer>;
  }
  const s = eventStyle(event);
  const durationMin = Math.max(0, Math.round((new Date(event.scheduled_end_at).getTime() - new Date(event.scheduled_start_at).getTime()) / 60000));
  const durationLabel = durationMin >= 60 ? `${Math.floor(durationMin / 60)}h${durationMin % 60 ? ` ${durationMin % 60}m` : ""}` : `${durationMin}m`;

  const canOpenClass = !!event.class_id;
  const openFullClassDetails = () => {
    if (!event.class_id) return;
    // class_id (template) → /class-schedules/:classId. Return URL uses
    // the STAFF STUDENT PROFILE so the class page has a safe back
    // target back into the calendar the teacher just clicked from.
    // occurrence_id is preserved as a query param so the class page's
    // attendance card can act on THIS specific session (not the next
    // arbitrary one) and Mark Attendance routes with the right id.
    const returnUrl = `/staff-portal/students/${studentId}`;
    navigate(`/class-schedules/${event.class_id}?occurrence_id=${event.occurrence_id}&return_url=${encodeURIComponent(returnUrl)}`);
    onClose();
  };

  const attLabel = (() => {
    const a = (event.attendance_status || "").toLowerCase();
    if (a === "present") return { label: "Present", bg: "#dcfce7", fg: "#166534" };
    if (a === "absent") return { label: "Absent", bg: "#fee2e2", fg: "#991b1b" };
    if (a === "late") return { label: "Late", bg: "#fef3c7", fg: "#92400e" };
    return { label: "Not Recorded", bg: "#f1f5f9", fg: "#475569" };
  })();

  // Grade list from the denormalized snapshot ("2, 3, 4" → chips).
  // Renders the "Class Supports" section that mirrors the admin
  // drawer's multi-grade breakdown when the class serves several grades.
  const gradeList: string[] = (event.grade || "").split(",").map((g) => g.trim()).filter(Boolean);
  const studentGrade = student?.grade ?? null;
  const studentName = student?.name ?? null;

  return (
    <Drawer
      anchor="right"
      open={!!event}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100vw", sm: 400 }, borderTopLeftRadius: isMobile ? 0 : 4, display: "flex", flexDirection: "column" } }}
    >
      {/* Dark navy header — matches the admin drawer visual grammar. */}
      <Box sx={{ background: `linear-gradient(135deg, #0f1e3c 0%, ${NAVY} 60%, ${NAVY_2} 100%)`, color: "#fff", p: 2.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }}>
            <Chip size="small" label={s.label} sx={{ bgcolor: "rgba(255,255,255,0.16)", color: "#fff", fontWeight: 800, fontSize: 10.5 }} />
            {event.schedule_type && event.schedule_type.toLowerCase() !== "one_time" && (
              <Chip size="small" label="Recurring" sx={{ bgcolor: "rgba(255,255,255,0.10)", color: "#fff", fontWeight: 700, fontSize: 10.5 }} />
            )}
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "#fff", p: 0.5, ml: 1 }}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
        <Typography sx={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em" }}>{event.subject || event.class_label}</Typography>
        <Typography sx={{ fontSize: 12, opacity: 0.85, mt: 0.25 }}>{fmtDateFull(event.scheduled_start_at)}</Typography>
      </Box>

      {/* Body — scrollable so the sticky footer stays visible. */}
      <Box sx={{ p: 2.5, flex: 1, overflowY: "auto" }}>
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>Class Information</Typography>
        <Stack spacing={1.5}>
          <DetailRow icon={<ScheduleOutlinedIcon />} label="Time" value={`${fmtTime(event.scheduled_start_at)} – ${fmtTime(event.scheduled_end_at)}`} />
          <DetailRow icon={<ScheduleOutlinedIcon />} label="Duration" value={durationLabel} />
          {event.staff_name && <DetailRow icon={<PersonOutlineIcon />} label="Staff" value={event.staff_name} />}
          {event.class_type && <DetailRow icon={<EventNoteOutlinedIcon />} label="Class Type" value={humanize(event.class_type)} />}
          {event.schedule_type && <DetailRow icon={<EventNoteOutlinedIcon />} label="Enrollment Type" value={humanize(event.schedule_type)} />}
          {event.teaching_mode && <DetailRow icon={<LaptopMacOutlinedIcon />} label="Study Mode" value={humanize(event.teaching_mode)} />}
          {event.location && <DetailRow icon={<LocationOnOutlinedIcon />} label="Location" value={event.location} />}
        </Stack>

        {studentName && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>This Student</Typography>
            <Box sx={{ p: 1.5, border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: "#f8fafc" }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: NAVY, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 12 }}>
                  {studentName.trim().split(/\s+/).map((n) => n[0]).join("").slice(0, 2).toUpperCase() || "?"}
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>{studentName}</Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }}>
                {studentGrade && <Chip size="small" icon={<SchoolOutlinedIcon />} label={`Grade ${studentGrade}`} sx={{ bgcolor: "#eef2ff", color: NAVY, fontWeight: 700, fontSize: 11 }} />}
                {event.subject && <Chip size="small" icon={<MenuBookOutlinedIcon />} label={event.subject} sx={{ bgcolor: "#f1f5f9", color: "#334155", fontWeight: 700, fontSize: 11 }} />}
                <Chip size="small" label="Enrolled" sx={{ bgcolor: "#dcfce7", color: "#166534", fontWeight: 700, fontSize: 11 }} />
              </Stack>
            </Box>
          </>
        )}

        {gradeList.length > 1 && event.subject && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>Class Supports</Typography>
            <Stack spacing={0.75}>
              {gradeList.map((g) => (
                <Stack key={g} direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={g} sx={{ bgcolor: "#eef2ff", color: NAVY, fontWeight: 800, fontSize: 11, height: 22, minWidth: 32 }} />
                  <Typography sx={{ fontSize: 13, color: "#334155", fontWeight: 600 }}>{event.subject}</Typography>
                </Stack>
              ))}
            </Stack>
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>Attendance</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <HowToRegOutlinedIcon sx={{ fontSize: 18, color: "#64748b" }} />
          <Chip size="small" label={attLabel.label} sx={{ bgcolor: attLabel.bg, color: attLabel.fg, fontWeight: 800, fontSize: 11 }} />
        </Stack>
      </Box>

      {/* Sticky footer CTA — class_id, not occurrence_id */}
      <Box sx={{ p: 2, borderTop: "1px solid #e2e8f0", bgcolor: "#fff" }}>
        <Button
          fullWidth
          variant="contained"
          onClick={openFullClassDetails}
          disabled={!canOpenClass}
          startIcon={<OpenInNewOutlinedIcon />}
          sx={{ textTransform: "none", fontWeight: 700, background: `linear-gradient(135deg, ${NAVY_2}, ${NAVY})` }}
        >
          View Full Class Details
        </Button>
        {!canOpenClass && (
          <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.75, textAlign: "center" }}>
            Class details are no longer available for this session.
          </Typography>
        )}
      </Box>
    </Drawer>
  );
};

const DetailRow: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({ icon, label, value }) => (
  <Stack direction="row" spacing={1.25} alignItems="flex-start">
    <Box sx={{ color: "#64748b", display: "flex", pt: 0.25, "& svg": { fontSize: 18 } }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</Typography>
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>{value}</Typography>
    </Box>
  </Stack>
);

const humanize = (s: string): string => {
  const cleaned = s.replace(/_/g, " ").trim();
  return cleaned.split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};

export default StaffStudentCalendar;
